const GOOGLE_FONT_WEIGHTS = '100;200;300;400;500;600;700;800;900';

/**
 * Converts a stored font key (e.g. "pt_mono") into a CSS/Google-Fonts family
 * name (e.g. "Pt+Mono"). Single-word keys are just capitalized.
 */
export function resolveFontFamily(fontKey: string | undefined): string {
	if (!fontKey) {
		return '';
	}

	return capitalizeFirstLetter(
		fontKey.toLowerCase().replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('_', '+')),
	);
}

export function formatFontKeyAsName(fontKey: string | undefined): string {
	return spacedFontName(resolveFontFamily(fontKey));
}

export function toCssFontFamily(fontFamilyName: string | undefined): string {
	return `"${spacedFontName(fontFamilyName || 'Arial')}", sans-serif`;
}

/**
 * Injects the Google Fonts <link> for the given family into <head> once.
 * Without this the element gets the correct `font-family` in CSS but renders
 * with a fallback until the font file is actually downloaded.
 */
export function loadGoogleFont(fontFamilyName: string): void {
	if (!fontFamilyName || typeof document === 'undefined') {
		return;
	}

	ensureHeadLink('amasty-google-fonts-preconnect', 'https://fonts.googleapis.com', 'preconnect');
	ensureHeadLink('amasty-google-fonts-static-preconnect', 'https://fonts.gstatic.com', 'preconnect', 'anonymous');
	ensureHeadLink(
		`amasty-google-font-${fontFamilyName}`,
		`https://fonts.googleapis.com/css2?family=${fontFamilyName}:wght@${GOOGLE_FONT_WEIGHTS}&display=swap`,
		'stylesheet',
	);
}

function ensureHeadLink(id: string, href: string, rel: string, crossOrigin?: string): void {
	if (document.getElementById(id)) {
		return;
	}

	const link = document.createElement('link');
	link.id = id;
	link.href = href;
	link.rel = rel;

	if (crossOrigin) {
		link.crossOrigin = crossOrigin;
	}

	document.head.appendChild(link);
}

function spacedFontName(fontFamilyName: string): string {
	return fontFamilyName.replaceAll('+', ' ');
}

function capitalizeFirstLetter(value: string): string {
	return value.charAt(0).toUpperCase() + value.slice(1);
}
